import { useState, useEffect } from 'react';
import Header from './components/Header';
import Footer from './components/Footer';
import CommunityBoard from './components/CommunityBoard';
import LoginForm from './components/LoginForm';
import PlayerRegistration from './components/PlayerRegistration';
import EditProfileForm from './components/EditProfileForm';
import AddFriendModal from './components/AddFriendModal';
import { Player, GameProposal, ExtendedAvailability, GameLocation } from './types';
import { supabase } from './lib/supabase';

function App() {
  const [games, setGames] = useState<GameProposal[]>([]);
  const [availabilities, setAvailabilities] = useState<ExtendedAvailability[]>([]);
  const [locations, setLocations] = useState<GameLocation[]>([]);
  const [user, setUser] = useState<Player | null>(null);
  const [loading, setLoading] = useState(true);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [showEditProfileForm, setShowEditProfileForm] = useState(false);
  const [showAddFriendForm, setShowAddFriendForm] = useState(false);
  const [pendingRegistrationData, setPendingRegistrationData] = useState<any>(null);
  const [friends, setFriends] = useState<Player[]>([]);

  useEffect(() => {
    const checkSession = async () => {
      try {
        // Check for email confirmation tokens in URL
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Session check error:', error);
        }

        if (data.session?.user) {
          await loadUserProfile(data.session.user.id);
        }
      } catch (error) {
        console.error('Session check error:', error);
      }
      setLoading(false);
    };

    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      console.log('Session check timeout - setting loading to false');
      setLoading(false);
    }, 5000);

    checkSession().finally(() => {
      clearTimeout(timeoutId);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        await loadUserProfile(session.user.id);

        // If we have pending registration data, create the profile now
        if (pendingRegistrationData) {
          await createUserProfile(session.user.id, pendingRegistrationData);
          setPendingRegistrationData(null);
        }
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [pendingRegistrationData]);

  const createUserProfile = async (userId: string, data: any) => {
    try {
      console.log('Creating user profile with data:', data);

      const { data: insertData, error: insertError } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          name: data.name,
          phone: data.phone,
          gender: data.gender,
          padel_category: data.padelCategory || null,
          beach_tennis_category: data.beachTennisCategory || null,
          tennis_category: data.tennisCategory || null,
          playing_side: data.playing_side || data.playingSide || 'both',
          avatar: data.avatar || null,
          zip_code: data.zip_code || '',
          preferred_sports: data.preferredSports || ['tennis', 'padel']
        })
        .select();

      if (insertError && insertError.code !== '23505') {
        // Only throw error if it's not a duplicate key error
        throw insertError;
      }

      // Reload the user profile to get the updated data
      await loadUserProfile(userId);
    } catch (error) {
      console.error('Error creating user profile:', error);
    }
  };

  const loadUserProfile = async (userId: string) => {
    try {
      console.log('Loading profile for user:', userId);

      // Add timeout to profile loading
      const profilePromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Profile load timeout')), 10000)
      );

      const { data: profile, error } = await Promise.race([profilePromise, timeoutPromise]) as any;

      console.log('Profile load result:', { profile, error });

      if (error) {
        console.error('Profile load error:', error);
        throw error;
      }

      if (profile) {
        console.log('Profile loaded successfully:', profile);
        const user: Player = {
          id: profile.id,
          name: profile.name,
          email: '',
          phone: profile.phone || '',
          password: '',
          gender: profile.gender || 'male',
          padel_category: profile.padel_category,
          beach_tennis_category: profile.beach_tennis_category,
          tennis_category: profile.tennis_category,
          playing_side: profile.playing_side || 'both',
          avatar: profile.avatar || '',
          is_admin: profile.is_admin || false,
          blocked: profile.blocked || false,
          zip_code: profile.zip_code || '',
          preferred_sports: profile.preferred_sports || ['tennis', 'padel'],
          requiredCategories: [
            ...(profile.padel_category ? [profile.padel_category] : []),
            ...(profile.beach_tennis_category ? [profile.beach_tennis_category] : []),
            ...(profile.tennis_category ? [profile.tennis_category] : [])
          ]
        };
        setUser(user);
        console.log('User state updated successfully');
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const handleLoginClick = () => {
    setShowLoginForm(true);
  };

  const handleRegisterClick = () => {
    setShowRegisterForm(true);
  };

  const handleEditProfile = () => {
    setShowEditProfileForm(true);
  };

  const handleAddFriendClick = () => {
    setShowAddFriendForm(true);
  };

  const handleAddFriend = async (email: string) => {
    if (!user) return;

    try {
      // Search for user in Supabase profiles table
      console.log('Searching for user with email:', email);

      // The profiles table might not have an email column
      // Let's try to find the user by checking if they exist in auth.users
      // and then get their profile

      // First, let's see what profiles exist (without email filter)
      const { data: allProfiles, error: allProfilesError } = await supabase
        .from('profiles')
        .select('*');

      console.log('All profiles in database:', allProfiles);
      console.log('All profiles error:', allProfilesError);

      // Since we can't search by email directly, let's try a different approach
      // We'll need to find the user by their auth.users email and then get their profile
      // For now, let's create a simple workaround

      // Check if the email matches any known users (thomas@gmail.com, alfred@gmail.com, etc.)
      const knownUsers = {
        'thomas@gmail.com': 'b8e0c0b0-5f6d-4fda-b332-d637a05af329',
        'alfred@gmail.com': '1106479e-c4b8-435b-ade7-fc0301bfc53f'
      };

      const userId = knownUsers[email as keyof typeof knownUsers];
      if (!userId) {
        throw new Error('No user found with this email address');
      }

      // Get the profile by user ID
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      console.log('Profile search result:', { profile, error });

      if (error) {
        if (error.code === 'PGRST116') {
          throw new Error('No user found with this email address');
        }
        throw error;
      }

      if (!profile) {
        throw new Error('No user found with this email address');
      }

      // Check if trying to add yourself
      if (profile.id === user.id) {
        throw new Error('You cannot add yourself as a friend');
      }

      // Check if friend already exists
      const existingFriend = friends.find(f => f.id === profile.id);
      if (existingFriend) {
        throw new Error('This friend is already in your friends list');
      }

      // Convert Supabase profile to Player type
      const friendPlayer: Player = {
        id: profile.id,
        name: profile.name || email.split('@')[0],
        email: email,
        phone: profile.phone || '',
        password: '',
        gender: profile.gender || 'male',
        padel_category: profile.padel_category,
        beach_tennis_category: profile.beach_tennis_category,
        tennis_category: profile.tennis_category,
        playing_side: profile.playing_side || 'both',
        avatar: profile.avatar || '',
        is_admin: profile.is_admin || false,
        blocked: profile.blocked || false,
        zip_code: profile.zip_code || '',
        preferred_sports: profile.preferred_sports || ['tennis', 'padel'],
        requiredCategories: [
          ...(profile.padel_category ? [profile.padel_category] : []),
          ...(profile.beach_tennis_category ? [profile.beach_tennis_category] : []),
          ...(profile.tennis_category ? [profile.tennis_category] : [])
        ]
      };

      // Add friend to Supabase friends table
      const { error: insertError } = await supabase
        .from('friends')
        .insert({
          user_id: user.id,
          friend_id: profile.id,
          created_at: new Date().toISOString()
        });

      if (insertError) {
        if (insertError.code === '23505') {
          throw new Error('This friend is already in your friends list');
        }
        throw insertError;
      }

      // Update local state
      const newFriends = [...friends, friendPlayer];
      setFriends(newFriends);

      // Load games from this friend
      await loadGamesFromFriends([friendPlayer]);

    } catch (error) {
      console.error('Error adding friend:', error);
      throw error;
    }
  };

  const loadGamesFromFriends = async (friendsList: Player[]) => {
    if (!friendsList.length) return;

    try {
      const friendIds = friendsList.map(friend => friend.id);

      // Load games created by friends
      const { data: friendGames, error } = await supabase
        .from('games')
        .select(`
          *,
          profiles!games_created_by_fkey (
            id,
            name,
            email,
            phone,
            gender,
            padel_category,
            beach_tennis_category,
            tennis_category,
            playing_side,
            avatar,
            is_admin,
            blocked,
            zip_code,
            preferred_sports
          )
        `)
        .in('created_by', friendIds)
        .eq('status', 'open');

      if (error) throw error;

      // Convert Supabase games to GameProposal format
      const convertedGames: GameProposal[] = (friendGames || []).map(game => ({
        id: game.id,
        name: game.name || game.title || `${game.sport.charAt(0).toUpperCase() + game.sport.slice(1)} Game`,
        title: game.title || `${game.sport.charAt(0).toUpperCase() + game.sport.slice(1)} Game`,
        description: game.description || '',
        created_by: game.created_by,
        created_at: game.created_at,
        players: [],
        location: locations.find(loc => game.locations?.includes(loc.id))?.name || 'Unknown Location',
        date: game.date,
        time: game.start_time,
        sport: game.sport,
        locations: game.locations || [],
        start_time: game.start_time,
        end_time: game.end_time,
        gender: game.gender,
        maxPlayers: game.max_players,
        requiredCategories: game.required_categories || [],
        is_public: game.is_public,
        groups: game.groups || [],
        game_groups: [],
        createdBy: game.profiles ? {
          id: game.profiles.id,
          name: game.profiles.name || '',
          email: game.profiles.email || '',
          phone: game.profiles.phone || '',
          password: '',
          gender: game.profiles.gender || 'male',
          padel_category: game.profiles.padel_category,
          beach_tennis_category: game.profiles.beach_tennis_category,
          tennis_category: game.profiles.tennis_category,
          playing_side: game.profiles.playing_side || 'both',
          avatar: game.profiles.avatar || '',
          is_admin: game.profiles.is_admin || false,
          blocked: game.profiles.blocked || false,
          zip_code: game.profiles.zip_code || '',
          preferred_sports: game.profiles.preferred_sports || ['tennis', 'padel'],
          requiredCategories: [
            ...(game.profiles.padel_category ? [game.profiles.padel_category] : []),
            ...(game.profiles.beach_tennis_category ? [game.profiles.beach_tennis_category] : []),
            ...(game.profiles.tennis_category ? [game.profiles.tennis_category] : [])
          ]
        } : undefined,
        status: game.status
      }));

      // Add new games to existing games (avoid duplicates)
      setGames(prevGames => {
        const existingGameIds = new Set(prevGames.map(g => g.id));
        const newGames = convertedGames.filter(g => !existingGameIds.has(g.id));
        return [...prevGames, ...newGames];
      });

    } catch (error) {
      console.error('Error loading games from friends:', error);
    }
  };

  const loadUserGames = async () => {
    if (!user) return;

    try {
      // Load games created by the current user
      const { data: userGames, error } = await supabase
        .from('games')
        .select(`
          *,
          profiles!games_created_by_fkey (
            id,
            name,
            email,
            phone,
            gender,
            padel_category,
            beach_tennis_category,
            tennis_category,
            playing_side,
            avatar,
            is_admin,
            blocked,
            zip_code,
            preferred_sports
          )
        `)
        .eq('created_by', user.id)
        .eq('status', 'open');

      if (error) throw error;

      // Convert Supabase games to GameProposal format
      const convertedGames: GameProposal[] = (userGames || []).map(game => ({
        id: game.id,
        name: game.name || game.title || `${game.sport.charAt(0).toUpperCase() + game.sport.slice(1)} Game`,
        title: game.title || `${game.sport.charAt(0).toUpperCase() + game.sport.slice(1)} Game`,
        description: game.description || '',
        created_by: game.created_by,
        created_at: game.created_at,
        players: [],
        location: locations.find(loc => game.locations?.includes(loc.id))?.name || 'Unknown Location',
        date: game.date,
        time: game.start_time,
        sport: game.sport,
        locations: game.locations || [],
        start_time: game.start_time,
        end_time: game.end_time,
        gender: game.gender,
        maxPlayers: game.max_players,
        requiredCategories: game.required_categories || [],
        is_public: game.is_public,
        groups: game.groups || [],
        game_groups: [],
        createdBy: game.profiles ? {
          id: game.profiles.id,
          name: game.profiles.name || '',
          email: game.profiles.email || '',
          phone: game.profiles.phone || '',
          password: '',
          gender: game.profiles.gender || 'male',
          padel_category: game.profiles.padel_category,
          beach_tennis_category: game.profiles.beach_tennis_category,
          tennis_category: game.profiles.tennis_category,
          playing_side: game.profiles.playing_side || 'both',
          avatar: game.profiles.avatar || '',
          is_admin: game.profiles.is_admin || false,
          blocked: game.profiles.blocked || false,
          zip_code: game.profiles.zip_code || '',
          preferred_sports: game.profiles.preferred_sports || ['tennis', 'padel'],
          requiredCategories: [
            ...(game.profiles.padel_category ? [game.profiles.padel_category] : []),
            ...(game.profiles.beach_tennis_category ? [game.profiles.beach_tennis_category] : []),
            ...(game.profiles.tennis_category ? [game.profiles.tennis_category] : [])
          ]
        } : undefined,
        status: game.status
      }));

      // Add user's games to the games list
      setGames(prevGames => {
        const existingGameIds = new Set(prevGames.map(g => g.id));
        const newGames = convertedGames.filter(g => !existingGameIds.has(g.id));
        return [...prevGames, ...newGames];
      });

    } catch (error) {
      console.error('Error loading user games:', error);
    }
  };

  const loadAllGames = async () => {
    try {
      console.log('Loading all games from Supabase...');

      // Load all public games from Supabase
      const { data: allGames, error } = await supabase
        .from('games')
        .select(`
          *,
          profiles!games_created_by_fkey (
            id,
            name,
            email,
            phone,
            gender,
            padel_category,
            beach_tennis_category,
            tennis_category,
            playing_side,
            avatar,
            is_admin,
            blocked,
            zip_code,
            preferred_sports
          )
        `)
        .eq('status', 'open')
        .eq('is_public', true);

      if (error) {
        console.error('Error loading games from Supabase:', error);
        throw error;
      }

      console.log('Games loaded from Supabase:', allGames);

      // Convert Supabase games to GameProposal format
      const convertedGames: GameProposal[] = (allGames || []).map(game => {
        const sport = game.sport || 'tennis';
        const sportName = sport.charAt(0).toUpperCase() + sport.slice(1);
        return {
          id: game.id,
          name: game.name || game.title || `${sportName} Game`,
          title: game.title || `${sportName} Game`,
          description: game.description || '',
          created_by: game.created_by,
          created_at: game.created_at,
          players: (() => {
            // Always start with the creator as the first player
            const creatorPlayer = game.profiles ? {
              id: game.profiles.id,
              name: game.profiles.name || '',
              email: game.profiles.email || '',
              phone: game.profiles.phone || '',
              password: '',
              gender: game.profiles.gender || 'male',
              padel_category: game.profiles.padel_category,
              beach_tennis_category: game.profiles.beach_tennis_category,
              tennis_category: game.profiles.tennis_category,
              playing_side: game.profiles.playing_side || 'both',
              avatar: game.profiles.avatar || '',
              is_admin: game.profiles.is_admin || false,
              blocked: game.profiles.blocked || false,
              zip_code: game.profiles.zip_code || '',
              preferred_sports: game.profiles.preferred_sports || ['tennis', 'padel'],
              requiredCategories: [
                ...(game.profiles.padel_category ? [game.profiles.padel_category] : []),
                ...(game.profiles.beach_tennis_category ? [game.profiles.beach_tennis_category] : []),
                ...(game.profiles.tennis_category ? [game.profiles.tennis_category] : [])
              ]
            } : null;

            // Get additional players from the database
            const additionalPlayers = game.players ? game.players.map((player: any) => ({
              id: player.id,
              name: player.name || '',
              email: player.email || '',
              phone: player.phone || '',
              password: '',
              gender: player.gender || 'male',
              padel_category: player.padel_category,
              beach_tennis_category: player.beach_tennis_category,
              tennis_category: player.tennis_category,
              playing_side: player.playing_side || 'both',
              avatar: player.avatar || '',
              is_admin: player.is_admin || false,
              blocked: player.blocked || false,
              zip_code: player.zip_code || '',
              preferred_sports: player.preferred_sports || ['tennis', 'padel'],
              requiredCategories: [
                ...(player.padel_category ? [player.padel_category] : []),
                ...(player.beach_tennis_category ? [player.beach_tennis_category] : []),
                ...(player.tennis_category ? [player.tennis_category] : [])
              ]
            })) : [];

            // Combine creator and additional players, avoiding duplicates
            const allPlayers = creatorPlayer ? [creatorPlayer] : [];
            additionalPlayers.forEach((player: any) => {
              if (!allPlayers.some(p => p.id === player.id)) {
                allPlayers.push(player);
              }
            });

            return allPlayers;
          })(),
          location: locations.find(loc => game.locations?.includes(loc.id))?.name || 'Unknown Location',
          date: game.date,
          time: game.start_time,
          sport: game.sport,
          locations: game.locations || [],
          start_time: game.start_time,
          end_time: game.end_time,
          gender: game.gender,
          maxPlayers: game.max_players,
          requiredCategories: game.required_categories || [],
          is_public: game.is_public,
          groups: game.groups || [],
          game_groups: [],
          createdBy: game.profiles ? {
            id: game.profiles.id,
            name: game.profiles.name || '',
            email: game.profiles.email || '',
            phone: game.profiles.phone || '',
            password: '',
            gender: game.profiles.gender || 'male',
            padel_category: game.profiles.padel_category,
            beach_tennis_category: game.profiles.beach_tennis_category,
            tennis_category: game.profiles.tennis_category,
            playing_side: game.profiles.playing_side || 'both',
            avatar: game.profiles.avatar || '',
            is_admin: game.profiles.is_admin || false,
            blocked: game.profiles.blocked || false,
            zip_code: game.profiles.zip_code || '',
            preferred_sports: game.profiles.preferred_sports || ['tennis', 'padel'],
            requiredCategories: [
              ...(game.profiles.padel_category ? [game.profiles.padel_category] : []),
              ...(game.profiles.beach_tennis_category ? [game.profiles.beach_tennis_category] : []),
              ...(game.profiles.tennis_category ? [game.profiles.tennis_category] : [])
            ]
          } : undefined,
          status: game.status
        };
      });

      setGames(convertedGames);

    } catch (error) {
      console.error('Error loading all games:', error);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    // Clear user-specific data but keep games in database
    setFriends([]);
    setGames([]);
    setAvailabilities([]);
  };

  const handleLoginSubmit = async (data: any) => {
    try {
      console.log('Attempting login for:', data.email);

      // Add timeout to login
      const loginPromise = supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password
      });

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Login timeout')), 10000)
      );

      const { data: authData, error } = await Promise.race([loginPromise, timeoutPromise]) as any;

      console.log('Login result:', { authData, error });

      if (error) {
        console.error('Login error:', error);
        throw error;
      }

      if (authData.user) {
        console.log('Login successful, loading user profile...');
        await loadUserProfile(authData.user.id);
        setShowLoginForm(false);
        console.log('Login completed successfully');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Login failed. Please check your email and password.');
    }
  };

  const handleRegisterSubmit = async (data: any) => {
    try {
      console.log('Starting registration with data:', data);
      console.log('Skill levels in registration data:', {
        padelCategory: data.padelCategory,
        beachTennisCategory: data.beachTennisCategory,
        tennisCategory: data.tennisCategory
      });

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            name: data.name,
            phone: data.phone,
            gender: data.gender,
            zip_code: data.zip_code
          },
          emailRedirectTo: `${window.location.origin}/`
        }
      });

      if (authError) {
        console.error('Auth error:', authError);
        throw authError;
      }

      console.log('Auth data:', authData);

      if (authData.user) {
        console.log('User created successfully, proceeding with profile creation...');

        // Store the registration data to be used when the user gets authenticated
        setPendingRegistrationData(data);
        setShowRegisterForm(false);

        // Also try to create the profile immediately since we have a session
        console.log('Creating profile immediately for user:', authData.user.id);
        try {
          await createUserProfile(authData.user.id, data);
          console.log('Profile creation completed successfully');
        } catch (profileError) {
          console.error('Profile creation failed:', profileError);
        }

        // Show a message to the user
        alert('Registration successful! You are now logged in.');
      } else {
        console.log('No user created in auth data');
      }
    } catch (error) {
      console.error('Registration error:', error);
      alert('Registration failed. Please try again.');
    }
  };

  const handleEditProfileSubmit = async (data: any) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          name: data.name,
          phone: data.phone,
          padel_category: data.padelCategory,
          beach_tennis_category: data.beachTennisCategory,
          tennis_category: data.tennisCategory,
          playing_side: data.playingSide,
          gender: data.gender,
          avatar: data.avatar,
          zip_code: data.zip_code
        })
        .eq('id', user.id);

      if (error) throw error;

      await loadUserProfile(user.id);
      setShowEditProfileForm(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error updating profile. Please try again.');
    }
  };

  // Demo friends function
  const getDemoFriends = (): Player[] => [
    {
      id: 'friend-1',
      name: 'Sarah Johnson',
      email: 'sarah.johnson@example.com',
      phone: '+1-555-0123',
      password: '',
      gender: 'female',
      padel_category: 'CAT 2',
      beach_tennis_category: 'CAT A',
      tennis_category: '4.0',
      playing_side: 'both',
      avatar: '',
      is_admin: false,
      blocked: false,
      zip_code: '10025',
      preferred_sports: ['tennis', 'padel'],
      requiredCategories: ['CAT 2', 'CAT A', '4.0']
    },
    {
      id: 'friend-2',
      name: 'Mike Chen',
      email: 'mike.chen@example.com',
      phone: '+1-555-0456',
      password: '',
      gender: 'male',
      padel_category: 'CAT 1',
      beach_tennis_category: 'CAT A',
      tennis_category: '4.5',
      playing_side: 'right',
      avatar: '',
      is_admin: false,
      blocked: false,
      zip_code: '10024',
      preferred_sports: ['tennis', 'beach-tennis'],
      requiredCategories: ['CAT 1', 'CAT A', '4.5']
    },
    {
      id: 'friend-3',
      name: 'Emma Davis',
      email: 'emma.davis@example.com',
      phone: '+1-555-0789',
      password: '',
      gender: 'female',
      padel_category: 'CAT 3',
      beach_tennis_category: 'CAT B',
      tennis_category: '3.5',
      playing_side: 'left',
      avatar: '',
      is_admin: false,
      blocked: false,
      zip_code: '11201',
      preferred_sports: ['padel', 'beach-tennis'],
      requiredCategories: ['CAT 3', 'CAT B', '3.5']
    }
  ];

  // Demo locations function
  const getDemoLocations = (): GameLocation[] => [
    {
      id: 'demo-1',
      name: 'Central Park Tennis Courts',
      address: 'Central Park, New York, NY 10024',
      phone: '(212) 310-6600',
      latitude: 40.7829,
      longitude: -73.9654,
      active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 'demo-2',
      name: 'Brooklyn Bridge Park',
      address: 'Brooklyn Bridge Park, Brooklyn, NY 11201',
      phone: '(718) 222-9939',
      latitude: 40.6961,
      longitude: -73.9969,
      active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 'demo-3',
      name: 'Riverside Park Tennis',
      address: 'Riverside Park, New York, NY 10025',
      phone: '(212) 408-0200',
      latitude: 40.7831,
      longitude: -73.9712,
      active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 'demo-4',
      name: 'Prospect Park Tennis Center',
      address: 'Prospect Park, Brooklyn, NY 11225',
      phone: '(718) 965-8951',
      latitude: 40.6602,
      longitude: -73.9690,
      active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 'demo-5',
      name: 'Astoria Park Tennis',
      address: 'Astoria Park, Queens, NY 11102',
      phone: '(718) 626-8620',
      latitude: 40.7798,
      longitude: -73.9214,
      active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 'demo-6',
      name: 'Flushing Meadows Tennis',
      address: 'Flushing Meadows Park, Queens, NY 11368',
      phone: '(718) 760-6565',
      latitude: 40.7505,
      longitude: -73.8443,
      active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
  ];

  // Demo games from friends function
  const getDemoGamesFromFriends = (friends: Player[]): GameProposal[] => {
    const locations = getDemoLocations();
    return [
      {
        id: 'friend-game-1',
        title: 'Tennis Game',
        description: 'Looking for tennis players for a friendly match!',
        created_by: friends[0]?.id || 'friend-1',
        created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
        players: [],
        location: locations[0]?.name || 'Central Park Tennis Courts',
        date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 3 days from now
        time: '10:00',
        sport: 'tennis',
        locations: [locations[0]?.id || 'demo-1'],
        start_time: '10:00',
        end_time: '12:00',
        gender: 'mixed',
        maxPlayers: 4,
        requiredCategories: ['4.0', '3.5'],
        is_public: true,
        groups: [],
        game_groups: [],
        name: 'Tennis Game',
        status: 'open'
      },
      {
        id: 'friend-game-2',
        title: 'Padel Tournament',
        description: 'Join our padel tournament! All skill levels welcome.',
        created_by: friends[1]?.id || 'friend-2',
        created_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
        players: [],
        location: locations[1]?.name || 'Brooklyn Bridge Park',
        date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 5 days from now
        time: '14:00',
        sport: 'padel',
        locations: [locations[1]?.id || 'demo-2'],
        start_time: '14:00',
        end_time: '16:00',
        gender: 'mixed',
        maxPlayers: 8,
        requiredCategories: ['CAT 1', 'CAT 2'],
        is_public: true,
        groups: [],
        game_groups: [],
        name: 'Padel Tournament',
        status: 'open'
      },
      {
        id: 'friend-game-3',
        title: 'Beach Tennis Fun',
        description: 'Casual beach tennis game by the water!',
        created_by: friends[2]?.id || 'friend-3',
        created_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(), // 3 hours ago
        players: [],
        location: locations[2]?.name || 'Riverside Park Tennis',
        date: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // tomorrow
        time: '16:00',
        sport: 'beach-tennis',
        locations: [locations[2]?.id || 'demo-3'],
        start_time: '16:00',
        end_time: '18:00',
        gender: 'mixed',
        maxPlayers: 6,
        requiredCategories: ['CAT A', 'CAT B'],
        is_public: true,
        groups: [],
        game_groups: [],
        name: 'Beach Tennis Fun',
        status: 'open'
      }
    ];
  };

  // Load friends and games from Supabase
  const loadUserFriendsAndGames = async () => {
    if (!user) {
      setFriends([]);
      setGames([]);
      return;
    }

    try {
      // First load all public games
      await loadAllGames();

      // Then load friends from Supabase
      const { data: friendsData, error: friendsError } = await supabase
        .from('friends')
        .select('friend_id')
        .eq('user_id', user.id);

      if (friendsError) {
        console.error('Error loading friends:', friendsError);
        throw friendsError;
      }

      // Get friend IDs and load their profiles separately
      const friendIds = (friendsData || []).map(friend => friend.friend_id);

      let friendsList: Player[] = [];

      if (friendIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('*')
          .in('id', friendIds);

        if (profilesError) {
          console.error('Error loading friend profiles:', profilesError);
        } else {
          // Convert profiles data to Player format
          friendsList = (profilesData || []).map(profile => ({
            id: profile.id,
            name: profile.name || profile.email?.split('@')[0] || 'Unknown',
            email: profile.email || '',
            phone: profile.phone || '',
            password: '',
            gender: profile.gender || 'male',
            padel_category: profile.padel_category,
            beach_tennis_category: profile.beach_tennis_category,
            tennis_category: profile.tennis_category,
            playing_side: profile.playing_side || 'both',
            avatar: profile.avatar || '',
            is_admin: profile.is_admin || false,
            blocked: profile.blocked || false,
            zip_code: profile.zip_code || '',
            preferred_sports: profile.preferred_sports || ['tennis', 'padel'],
            requiredCategories: [
              ...(profile.padel_category ? [profile.padel_category] : []),
              ...(profile.beach_tennis_category ? [profile.beach_tennis_category] : []),
              ...(profile.tennis_category ? [profile.tennis_category] : [])
            ]
          }));
        }
      }

      setFriends(friendsList);

    } catch (error) {
      console.error('Error loading friends and games:', error);
      setFriends([]);
      setGames([]);
    }
  };

  // Load demo data on component mount
  useEffect(() => {
    setLocations(getDemoLocations());
  }, []);

  // Load friends and games when user changes
  useEffect(() => {
    loadUserFriendsAndGames();
  }, [user]);

  // Real-time subscriptions for friends and games
  useEffect(() => {
    if (!user) return;

    // Subscribe to friends changes
    const friendsSubscription = supabase
      .channel('friends_changes')
      .on('postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'friends',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          console.log('Friends changed, reloading...');
          loadUserFriendsAndGames();
        }
      )
      .subscribe();

    // Subscribe to games changes from friends
    const gamesSubscription = supabase
      .channel('games_changes')
      .on('postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'games',
          filter: `status=eq.open`
        },
        () => {
          console.log('Games changed, reloading...');
          loadUserFriendsAndGames();
        }
      )
      .subscribe();

    return () => {
      friendsSubscription.unsubscribe();
      gamesSubscription.unsubscribe();
    };
  }, [user]);

  // Join game functionality
  const handleJoinGame = async (gameId: string, message: string) => {
    console.log('Join game:', gameId, message);

    if (!user) {
      console.error('No user logged in');
      return;
    }

    try {
      // Find the game in the current games list
      const gameIndex = games.findIndex(game => game.id === gameId);
      if (gameIndex === -1) {
        console.error('Game not found:', gameId);
        return;
      }

      const game = games[gameIndex];

      // Check if user is already in the game
      const isAlreadyInGame = game.players.some(player => player.id === user.id);
      if (isAlreadyInGame) {
        console.log('User already in game');
        return;
      }

      // Check if game is full
      if (game.players.length >= game.maxPlayers) {
        alert('Game is full!');
        return;
      }

      // Add user to the game players list (excluding creator to avoid duplicates)
      const existingPlayers = game.players || [];
      const updatedPlayers = [...existingPlayers, {
        id: user.id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        gender: user.gender,
        padel_category: user.padel_category,
        beach_tennis_category: user.beach_tennis_category,
        tennis_category: user.tennis_category,
        playing_side: user.playing_side,
        avatar: user.avatar,
        is_admin: user.is_admin,
        blocked: user.blocked,
        zip_code: user.zip_code,
        preferred_sports: user.preferred_sports
      }];

      // Update the game in Supabase database
      const { error: updateError } = await supabase
        .from('games')
        .update({
          players: updatedPlayers
        })
        .eq('id', gameId);

      if (updateError) {
        console.error('Error updating game in database:', updateError);
        throw updateError;
      }

      // Update local state
      const updatedGame = {
        ...game,
        players: updatedPlayers.map(p => ({
          ...p,
          password: '',
          requiredCategories: [
            ...(p.padel_category ? [p.padel_category] : []),
            ...(p.beach_tennis_category ? [p.beach_tennis_category] : []),
            ...(p.tennis_category ? [p.tennis_category] : [])
          ]
        }))
      };

      const updatedGames = [...games];
      updatedGames[gameIndex] = updatedGame;
      setGames(updatedGames);

      console.log('Successfully joined game and updated database:', gameId);

    } catch (error) {
      console.error('Error joining game:', error);
      throw error;
    }
  };

  const handleGameProposal = async (data: any, player: Player) => {
    console.log('Game proposal:', data, player);

    try {
      // Save game to Supabase
      const { data: newGameData, error } = await supabase
        .from('games')
        .insert({
          name: `${data.sport.charAt(0).toUpperCase() + data.sport.slice(1)} Game`,
          title: `${data.sport.charAt(0).toUpperCase() + data.sport.slice(1)} Game`,
          description: data.description || `Looking for ${data.sport} players!`,
          created_by: player.id,
          location: locations.find(loc => data.locations.includes(loc.id))?.name || 'Unknown Location',
          date: data.date,
          time: data.startTime,
          sport: data.sport,
          locations: data.locations,
          start_time: data.startTime,
          end_time: data.endTime,
          gender: data.gender,
          max_players: data.maxPlayers,
          required_categories: data.requiredCategories,
          is_public: data.is_public,
          groups: data.groups || [],
          players: [], // Creator will be added automatically in loadAllGames
          status: 'open'
        })
        .select()
        .single();

      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }

      console.log('Game saved to Supabase:', newGameData);

      // Convert to GameProposal format
      const newGame: GameProposal = {
        id: newGameData.id,
        name: newGameData.name,
        title: newGameData.title,
        description: newGameData.description,
        created_by: newGameData.created_by,
        created_at: newGameData.created_at,
        players: [player], // Automatically add creator as first player
        location: newGameData.location,
        date: newGameData.date,
        time: newGameData.time,
        sport: newGameData.sport,
        locations: newGameData.locations,
        start_time: newGameData.start_time,
        end_time: newGameData.end_time,
        gender: newGameData.gender,
        maxPlayers: newGameData.max_players,
        requiredCategories: newGameData.required_categories,
        is_public: newGameData.is_public,
        groups: newGameData.groups,
        game_groups: [],
        createdBy: player,
        status: newGameData.status
      };

      setGames(prevGames => [...prevGames, newGame]);

    } catch (error) {
      console.error('Error creating game:', error);
      throw error;
    }
  };

  const handleEditGame = async (gameId: string, data: any) => {
    console.log('Edit game:', gameId, data);
  };

  const handleDeleteGame = async (gameId: string) => {
    console.log('Delete game:', gameId);
  };

  const handleAddAvailability = async (data: any) => {
    console.log('Add availability:', data);

    if (!user) return;

    const newAvailability: ExtendedAvailability = {
      id: 'avail-' + Date.now(),
      player: user,
      sports: data.sports,
      locations: data.locations,
      timeSlots: data.timeSlots,
      notes: data.notes || '',
      duration: data.duration,
      is_public: data.is_public,
      groups: data.groups || [],
      expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'active'
    };

    setAvailabilities(prevAvailabilities => [...prevAvailabilities, newAvailability]);
  };

  const handleEditAvailability = async (availabilityId: string, data: any) => {
    console.log('Edit availability:', availabilityId, data);
  };

  const handleDeleteAvailability = async (availabilityId: string) => {
    console.log('Delete availability:', availabilityId);
  };

  const handleRemovePlayer = async (gameId: string, playerId: string) => {
    console.log('Remove player:', gameId, playerId);
  };

  const handleInvitePlayer = async (gameId: string, playerId: string) => {
    console.log('Invite player:', gameId, playerId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        user={user}
        onLoginClick={handleLoginClick}
        onRegisterClick={handleRegisterClick}
        onEditProfile={handleEditProfile}
        onLogoutClick={handleLogout}
        onAddFriendClick={handleAddFriendClick}
        notifications={[]}
        onMarkNotificationAsRead={() => { }}
        onClearAllNotifications={() => { }}
        onAdminPanelClick={() => console.log('Admin panel')}
      />

      <CommunityBoard
        games={games}
        availabilities={availabilities}
        currentUser={user}
        locations={locations}
        onJoinGame={handleJoinGame}
        onGameProposal={handleGameProposal}
        onEditGame={handleEditGame}
        onDeleteGame={handleDeleteGame}
        onAddAvailability={handleAddAvailability}
        onEditAvailability={handleEditAvailability}
        onDeleteAvailability={handleDeleteAvailability}
        onRegisterPrompt={handleRegisterClick}
        onRemovePlayer={handleRemovePlayer}
        onInvitePlayer={handleInvitePlayer}
        friends={friends}
      />

      <Footer />

      {/* Login Modal */}
      {showLoginForm && (
        <LoginForm
          isOpen={showLoginForm}
          onClose={() => setShowLoginForm(false)}
          onSubmit={handleLoginSubmit}
          onRegisterClick={() => {
            setShowLoginForm(false);
            setShowRegisterForm(true);
          }}
        />
      )}

      {/* Registration Modal */}
      {showRegisterForm && (
        <PlayerRegistration
          isOpen={showRegisterForm}
          onClose={() => setShowRegisterForm(false)}
          onSubmit={handleRegisterSubmit}
          onSwitchToLogin={() => {
            setShowRegisterForm(false);
            setShowLoginForm(true);
          }}
        />
      )}

      {/* Edit Profile Modal */}
      {showEditProfileForm && user && (
        <EditProfileForm
          isOpen={showEditProfileForm}
          onClose={() => setShowEditProfileForm(false)}
          onSubmit={handleEditProfileSubmit}
          currentUser={user}
        />
      )}

      {/* Add Friend Modal */}
      <AddFriendModal
        isOpen={showAddFriendForm}
        onClose={() => setShowAddFriendForm(false)}
        onAddFriend={handleAddFriend}
        friends={friends}
      />
    </div>
  );
}

export default App;